// Library Methods
import { useCallback, useMemo } from "react";

// Components
import Skeleton from "@mui/material/Skeleton";
import ChartCross from "src/assets/shapes/ChartCross/ChartCross";
import QuadrantBuilder from "./QuadrantBuilder/QuadrantBuilder";

// Constants
import { eyeTypeEnums } from "src/constants/enums";

// Types
import { ChartSpotType } from "src/services/exams/types";

// Utils
import { buildQuadrantGrid } from "src/utils/exams/examReportsHelper";

const DEFAULT_CONTAINER_SIZE = 312;

type PatternGraphType = {
  pressedPoints: Array<ChartSpotType>;
  eyeType: string;
  gridType: string | undefined;
  containerSize?: number;
};

const PatternGraph = ({
  pressedPoints,
  eyeType,
  gridType,
  containerSize = DEFAULT_CONTAINER_SIZE,
}: PatternGraphType) => {
  const { LEFT, RIGHT } = eyeTypeEnums;

  const size = useMemo(() => containerSize * 0.9, [containerSize]);
  const tickSize = useMemo(() => size / 104, [size]);

  // method to order the points by the event X value asc
  const compareXAxis = useCallback((a: ChartSpotType, b: ChartSpotType) => {
    const xPositionA = a.x;
    const xPositionB = b.x;

    if (xPositionA < xPositionB) {
      return -1;
    }
    if (xPositionA > xPositionB) {
      return 1;
    }
    return 0;
  }, []);

  // sort each point by x and y position
  const sortedPoints = useMemo(() => {
    const sortPoints = (points: Array<ChartSpotType>) =>
      points.sort(compareXAxis);

    return {
      topLeft: sortPoints(pressedPoints.filter(({ x, y }) => y >= 0 && x <= 0)),
      topRight: sortPoints(pressedPoints.filter(({ x, y }) => y >= 0 && x > 0)),
      bottomLeft: sortPoints(
        pressedPoints.filter(({ x, y }) => y < 0 && x <= 0)
      ),
      bottomRight: sortPoints(
        pressedPoints.filter(({ x, y }) => y < 0 && x > 0)
      ),
    };
  }, [pressedPoints, compareXAxis]);

  // should be an array of arrays, based on the columns and rows defined on the quadrant builder
  const quadrantValues = useMemo(() => {
    const buildGrid = (points: Array<ChartSpotType>, sectionSide: string) =>
      points.length
        ? buildQuadrantGrid({ gridType, sectionSide, eyeType, points })
        : [];

    return {
      topLeft: buildGrid(sortedPoints.topLeft, LEFT),
      topRight: buildGrid(sortedPoints.topRight, RIGHT),
      bottomLeft: buildGrid(sortedPoints.bottomLeft, LEFT),
      bottomRight: buildGrid(sortedPoints.bottomRight, RIGHT),
    };
  }, [gridType, eyeType, sortedPoints, LEFT, RIGHT]);

  // Show loading skeleton if data is not yet processed
  if (
    quadrantValues.bottomRight.length === 0 ||
    quadrantValues.bottomLeft.length === 0 ||
    quadrantValues.topRight.length === 0 ||
    quadrantValues.topLeft.length === 0
  ) {
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={DEFAULT_CONTAINER_SIZE}
        sx={{
          borderRadius: "4px",
        }}
      />
    );
  }

  return (
    <div
      style={{
        height: `${size}px`,
        width: `${size}px`,
        position: "relative",
      }}
    >
      <ChartCross
        gridType={gridType}
        width={size}
        height={size}
        tickSize={tickSize}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: "2px",
          height: `${size}px`,
          width: `${size}px`,
        }}
      >
        {/* Top left section */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",

            paddingRight: "4px",
            paddingBottom: "4px",
          }}
        >
          <QuadrantBuilder
            gridValues={quadrantValues.topLeft}
            containerSize={containerSize}
            mirrorHorizontally
          />
        </div>

        {/* Top right section */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-end",

            paddingLeft: "4px",
            paddingBottom: "4px",
          }}
        >
          <QuadrantBuilder
            gridValues={quadrantValues.topRight}
            containerSize={containerSize}
          />
        </div>

        {/* Bottom left section */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",

            paddingRight: "4px",
            paddingTop: "4px",
          }}
        >
          <QuadrantBuilder
            gridValues={quadrantValues.bottomLeft}
            containerSize={containerSize}
            mirrorHorizontally
            mirrorVertically
          />
        </div>

        {/* Bottom right section */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",

            paddingLeft: "4px",
            paddingTop: "4px",
          }}
        >
          <QuadrantBuilder
            gridValues={quadrantValues.bottomRight}
            containerSize={containerSize}
            mirrorVertically
          />
        </div>
      </div>
    </div>
  );
};

export default PatternGraph;
